import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { getDisplayVerdict } from '../../../../lib/reportVerdict';
import { REPORT_TYPE_REGISTRY } from '../../../../config/reportTypes';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialDefinitionList, EditorialRecommendations } from '../../report/editorial';

/**
 * Max campus capacity — the testFit schema doesn't carry a dedicated MW/SF
 * field, so we surface the recommendations list (which typically contains
 * sizing actions). It used to also show `conclusion.overallScore` as a
 * "layout score" proxy.
 *
 * Score Source-of-Truth S5 — test-fit is THE exception. Its ReportPromptConfig
 * has `usesScoring: false`: there is no deterministic scorer for it, so
 * `report.overallScore` is the constant `: 50` literal fallback in
 * siteRagService and the (now-removed) LLM `conclusion.overallScore` was an
 * unbounded drift. A constant 50/100 — or any LLM number — is misleading, so
 * getDisplayScore short-circuits on `reportType === 'test-fit'` and returns
 * `source: 'none'`; this surface then renders "—" / hides the row. The
 * recommendations list is the substantive content here. (If a deterministic
 * test-fit scorer is ever added, relax the guard in src/lib/reportScore.ts —
 * this component already does the right thing once that returns a real value.)
 *
 * W1c editorial port — the hand-rolled verdict row (text-xs uppercase
 * tracking-widest) becomes a one-item EditorialDefinitionList and the
 * accent-dot <ul> becomes EditorialRecommendations (decimal markers).
 * The flat string list has no label/value shape, so DefinitionList /
 * Datasheet don't fit it; EditorialRecommendations is the closest-fit
 * primitive (purpose-built for flat recommendation lines, drops
 * empty/whitespace entries — never fabricates content).
 */
export const MaxCapacitySection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const recs = report.testFitAnalysis?.recommendations ?? [];
  // Sentiment-First Verdict SF2 — replaces the legacy `getDisplayScore`
  // (which always returned null for test-fit anyway). When a deterministic
  // test-fit scorer is added (`usesScoring: true`), `getDisplayVerdict` will
  // produce a real tier + label here; today it returns `tier: 'none'` and
  // the verdict row stays hidden.
  const verdict = getDisplayVerdict(report);
  const hasData = recs.length > 0 || verdict.tier !== 'none';
  return (
    <SectionShell label={label} hasData={hasData}>
      {/* SF2: only render the verdict row when getDisplayVerdict resolves to
          a real tier. For test-fit (usesScoring: false) tier === 'none' and
          the row stays hidden. Prefix kept ("Test Fit verdict") so a future
          deterministic test-fit scorer surfaces unambiguously. */}
      {verdict.tier !== 'none' && (
        <EditorialDefinitionList
          theme={theme}
          items={[{
            label: `${REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility'].shortLabel} verdict`,
            value: verdict.label,
          }]}
        />
      )}
      {recs.length > 0 && (
        <EditorialRecommendations
          theme={theme}
          heading="Sizing recommendations"
          marker="decimal"
          items={recs}
        />
      )}
    </SectionShell>
  );
};
